import { ComponentProps, RefObject } from 'preact';
import { useCallback, useLayoutEffect, useState } from 'preact/hooks';

import { TextFieldInput } from '#UNSAFE_TextField';
import { ValueUpdateDetail } from '#utils/UNSAFE_valueUpdateDetail';

type UseTextAreaAltEnterOptions = {
  /**
   * The onInput of the TextFieldInput
   */
  onInput: NonNullable<ComponentProps<typeof TextFieldInput>['onInput']>;
  /**
   * The ref to the textarea element
   */
  textAreaRef: RefObject<HTMLTextAreaElement>;
};

/**
 * A custom hook to enable an alternate way for entering a new line in the TextArea components using
 * Alt + Enter (or option + return in MacOS). This is particularly useful in scenarios where pressing Enter
 * could mean a different action, like exiting from edit mode in a Table/DataGrid containing a TextArea.
 */
export const useTextAreaAltEnter = ({ onInput, textAreaRef }: UseTextAreaAltEnterOptions) => {
  // We want to have alt/option+Enter to insert a newline like Enter does.
  const [selectionStart, setSelectionStart] = useState<number>();
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // If we see an alt+enter (or option+return on a Mac), we insert a newline and call onInputAndDispatch
      // since that is what is called any time the input value changes.
      if (event.altKey && event.key === 'Enter') {
        event.preventDefault(); // Prevent default Enter behavior
        const textarea = event.target as HTMLTextAreaElement;
        const { selectionStart: start, selectionEnd: end, value: previousValue } = textarea;
        const newValue = previousValue.substring(0, start) + '\n' + previousValue.substring(end);

        const valueUpdateDetail: ValueUpdateDetail<string> = {
          previousValue: previousValue,
          value: newValue
        };
        setSelectionStart(start + 1);
        onInput(valueUpdateDetail);
      }
    },
    [onInput]
  );

  useLayoutEffect(() => {
    if (selectionStart) {
      textAreaRef.current?.setSelectionRange(selectionStart, selectionStart);
      // reset to undefined, so the next selectionStart will trigger the useLayoutEffect
      // even if it is the same as the previous selectionStart that was set.  This is
      // because we are only setting selectionStart for alt+enter.
      setSelectionStart(undefined);
    }
  }, [selectionStart, textAreaRef]);

  return { handleKeyDown };
};
